/**
 * `<caos-compare-view>` — what two environments say about the same things, narrowed four ways.
 * (Catalog: Developer surfaces › CompareView — the result of comparing two environments, filtered by type, item and agreement.)
 *
 * THE AGREEMENT VOCABULARY IS THE POINT. In both, different · In both, equal · Only in the source ·
 * Only in the target. Four phrases saying precisely what a comparison of two things can produce,
 * with no ambiguity and nothing left out — the best vocabulary in the tool this was measured
 * against, and it is repeated here verbatim rather than compressed into "changed / added / removed",
 * which loses the distinction between "the same" and "not compared".
 *
 * THE DEFAULT HIDES THE EQUAL ONES. A comparison of two environments returns thousands of items and
 * almost all of them agree; a list that leads with them buries the answer. Equal items are one press
 * away and the summary always says how many were hidden, so nobody mistakes the short list for the
 * whole comparison.
 *
 * THE COUNTS ARE OVER THE WHOLE COMPARISON, never the filtered view. What somebody needs from the
 * top line is how big the difference is, and a count that shrank as they filtered would answer a
 * question they did not ask.
 *
 * A DIFFERENCE IS OPENED, NOT SHOWN INLINE. This surface answers "what disagrees"; the side-by-side
 * belongs to `<caos-difference-modal>`, which is a different question and a much taller one.
 *
 * Styling seams: `::part(compare)`, `::part(filters)`, `::part(filter)`, `::part(search)`,
 * `::part(summary)`, `::part(table)`, `::part(agreement)`, `::part(open)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import type { CaosDataTable, CaosSortDirection, CaosTableRow } from './data-table.js';

/** What a comparison of one item can produce. */
export type CaosAgreement = 'both-different' | 'both-equal' | 'only-source' | 'only-target';

/**
 * The four phrases, verbatim. Exported so a surface says the same words everywhere, and so the
 * distinction is never quietly compressed into three.
 */
export const AGREEMENT_LABELS: Readonly<Record<CaosAgreement, string>> = {
  'both-different': 'In both, different',
  'both-equal': 'In both, equal',
  'only-source': 'Only in the source',
  'only-target': 'Only in the target',
};

/** One thing that was compared. */
export interface CaosCompareItem {
  /** What kind of thing it is. */
  readonly type: string;
  /** Its name. */
  readonly name: string;
  readonly agreement: CaosAgreement;
}

const sheet = makeSheet(`
  :host { display: block; }

  .compare { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .filters { display: flex; gap: var(--caos-space-2); flex-wrap: wrap; align-items: center; }
  .filters select, .filters input {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .filters input { flex: 1 1 180px; min-width: 140px; }
  .filters select:focus-visible, .filters input:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .summary { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
  .summary .differ { color: var(--caos-color-danger-text); font-weight: 600; }

  .empty { font-family: var(--caos-font); font-size: 13px; color: var(--caos-color-text-muted); }
`);

/** How many items fall into each of the four answers. */
export function tallyAgreement(items: readonly CaosCompareItem[]): Record<CaosAgreement, number> {
  const out: Record<CaosAgreement, number> = {
    'both-different': 0,
    'both-equal': 0,
    'only-source': 0,
    'only-target': 0,
  };
  for (const item of items) if (item.agreement in out) out[item.agreement] += 1;
  return out;
}

export class CaosCompareView extends CaosElement {
  static get observedAttributes(): string[] {
    return ['type', 'agreement', 'search', 'show-equal', 'sort-key', 'sort-direction', 'empty-text'];
  }

  private filtersEl: HTMLDivElement;
  private summaryEl: HTMLParagraphElement;
  private tableEl: CaosDataTable;
  private emptyEl: HTMLParagraphElement;

  private data: readonly CaosCompareItem[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'compare';
    wrap.setAttribute('part', 'compare');

    this.filtersEl = document.createElement('div');
    this.filtersEl.className = 'filters';
    this.filtersEl.setAttribute('part', 'filters');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.emptyEl = document.createElement('p');
    this.emptyEl.className = 'empty';
    this.emptyEl.hidden = true;

    this.tableEl = document.createElement('caos-data-table') as CaosDataTable;
    this.tableEl.setAttribute('part', 'table');
    this.tableEl.addEventListener('caos-sort', (e) => {
      const { column, direction } = (e as CustomEvent<{ column: string; direction: CaosSortDirection }>).detail;
      e.stopPropagation();
      this.setAttribute('sort-key', column);
      this.setAttribute('sort-direction', direction);
    });

    wrap.append(this.filtersEl, this.summaryEl, this.emptyEl, this.tableEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get items(): readonly CaosCompareItem[] {
    return this.data;
  }

  set items(next: readonly CaosCompareItem[]) {
    this.data = Array.isArray(next) ? next : [];
    this.render();
  }

  /** How many of each answer, over the WHOLE comparison — never the filtered view. */
  get tally(): Record<CaosAgreement, number> {
    return tallyAgreement(this.data);
  }

  /** The items that disagree or exist on only one side — everything that is not "equal". */
  get differing(): readonly CaosCompareItem[] {
    return this.data.filter((i) => i.agreement !== 'both-equal');
  }

  /** The distinct types compared, in the order they first occur. */
  get types(): readonly string[] {
    const seen: string[] = [];
    for (const i of this.data) if (!seen.includes(i.type)) seen.push(i.type);
    return seen;
  }

  /** The items surviving every filter, in the current order. */
  get visibleItems(): readonly CaosCompareItem[] {
    const type = this.getAttribute('type');
    const agreement = this.getAttribute('agreement');
    const term = (this.getAttribute('search') ?? '').trim().toLowerCase();
    // Equal items are hidden by default. A comparison returns thousands and almost all agree; a
    // list that leads with them buries the answer.
    const showEqual = this.hasAttribute('show-equal') || agreement === 'both-equal';

    const kept = this.data.filter((i) => {
      if (type !== null && type !== '' && i.type !== type) return false;
      if (agreement !== null && agreement !== '' && i.agreement !== agreement) return false;
      if (!showEqual && i.agreement === 'both-equal') return false;
      if (term !== '' && !i.name.toLowerCase().includes(term) && !i.type.toLowerCase().includes(term)) return false;
      return true;
    });

    const key = this.getAttribute('sort-key') ?? 'name';
    const sign = this.getAttribute('sort-direction') === 'desc' ? -1 : 1;
    return [...kept].sort((a, b) => {
      const read = (i: CaosCompareItem): string =>
        key === 'agreement' ? AGREEMENT_LABELS[i.agreement] : String((i as unknown as Record<string, unknown>)[key] ?? '');
      const cmp = read(a).localeCompare(read(b), undefined, { numeric: true, sensitivity: 'base' });
      return (cmp !== 0 ? cmp : `${a.type}.${a.name}`.localeCompare(`${b.type}.${b.name}`)) * sign;
    });
  }

  private open(item: CaosCompareItem): void {
    this.dispatchEvent(new CustomEvent('caos-compare-open', { bubbles: true, composed: true, detail: { item } }));
  }

  private render(): void {
    this.renderFilters();

    const visible = this.visibleItems;
    const nothing = this.data.length === 0;
    this.emptyEl.hidden = !nothing && visible.length !== 0;
    this.tableEl.hidden = visible.length === 0;
    this.emptyEl.textContent = nothing
      ? (this.getAttribute('empty-text') ?? 'Nothing has been compared yet.')
      : 'No items match the filters.';

    const t = this.tally;
    const nodes: Node[] = [];
    if (!nothing) {
      const differ = t['both-different'] + t['only-source'] + t['only-target'];
      const strong = document.createElement('strong');
      strong.className = differ > 0 ? 'differ' : '';
      strong.textContent = `${differ} of ${this.data.length} differ`;
      nodes.push(strong);
      nodes.push(
        document.createTextNode(
          ` · ${t['both-different']} different · ${t['only-source']} only in the source · ` +
            `${t['only-target']} only in the target`,
        ),
      );
      if (t['both-equal'] > 0 && !this.hasAttribute('show-equal') && this.getAttribute('agreement') !== 'both-equal') {
        // Always said. Nobody should be able to mistake the short list for the whole comparison.
        nodes.push(document.createTextNode(` · ${t['both-equal']} equal, hidden`));
      }
    }
    this.summaryEl.replaceChildren(...nodes);

    if (visible.length === 0) return;

    this.tableEl.columns = [
      { key: 'type', label: 'Type', sortable: true, width: '22%' },
      { key: 'name', label: 'Item', sortable: true },
      { key: 'agreement', label: 'Comparison', sortable: true, width: '26%' },
      { key: 'open', label: '', width: '12%' },
    ];
    this.tableEl.sort = {
      key: this.getAttribute('sort-key') ?? 'name',
      direction: this.getAttribute('sort-direction') === 'desc' ? 'desc' : 'asc',
    };
    this.tableEl.rows = visible.map((i) => this.rowFor(i));
  }

  private renderFilters(): void {
    const fill = (
      part: string,
      label: string,
      attr: string,
      anyLabel: string,
      values: ReadonlyArray<{ v: string; t: string }>,
    ): HTMLSelectElement => {
      const select = document.createElement('select');
      select.setAttribute('part', part);
      select.setAttribute('aria-label', label);
      const chosen = this.getAttribute(attr) ?? '';
      for (const { v, t } of [{ v: '', t: anyLabel }, ...values]) {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = t;
        o.selected = v === chosen;
        select.append(o);
      }
      select.addEventListener('change', () => {
        if (select.value === '') this.removeAttribute(attr);
        else this.setAttribute(attr, select.value);
        this.dispatchEvent(
          new CustomEvent('caos-compare-filter', {
            bubbles: true,
            composed: true,
            detail: { type: this.getAttribute('type'), agreement: this.getAttribute('agreement') },
          }),
        );
      });
      return select;
    };

    const typeSelect = fill('filter', 'Metadata type', 'type', 'Any type', this.types.map((t) => ({ v: t, t })));
    const agreementSelect = fill(
      'filter',
      'Comparison result',
      'agreement',
      'Anything that differs',
      (Object.keys(AGREEMENT_LABELS) as CaosAgreement[]).map((a) => ({ v: a, t: AGREEMENT_LABELS[a] })),
    );

    const search = document.createElement('input');
    search.type = 'search';
    search.setAttribute('part', 'search');
    search.placeholder = 'Search items';
    search.setAttribute('aria-label', 'Search compared items by name or type');
    search.value = this.getAttribute('search') ?? '';
    search.addEventListener('input', () => this.setAttribute('search', search.value));

    this.filtersEl.replaceChildren(typeSelect, agreementSelect, search);
  }

  private rowFor(item: CaosCompareItem): CaosTableRow {
    const agreement = document.createElement('span');
    agreement.setAttribute('part', 'agreement');
    // The phrase, in full. Compressing it into "changed" loses the distinction between "the same"
    // and "not on the other side at all".
    agreement.textContent = AGREEMENT_LABELS[item.agreement];
    if (item.agreement === 'both-equal') agreement.className = 'caos-table__cell-note';

    let openCell: unknown = '';
    if (item.agreement !== 'both-equal') {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'caos-table__idlink';
      button.setAttribute('part', 'open');
      button.textContent = 'Compare';
      button.setAttribute('aria-label', `Compare ${item.type} ${item.name} side by side`);
      button.addEventListener('click', () => this.open(item));
      openCell = { node: button };
    }

    return {
      id: `${item.type}.${item.name}`,
      cells: { type: item.type, name: item.name, agreement: { node: agreement }, open: openCell },
    } as CaosTableRow;
  }
}

define('caos-compare-view', CaosCompareView);

declare global {
  interface HTMLElementTagNameMap {
    'caos-compare-view': CaosCompareView;
  }
}
